/**
 * Sync-aware Database Schema
 *
 * Extended schema with sync metadata and user-specific database support.
 */

import Dexie, { type EntityTable } from "dexie";
import type {
  LogEntry,
  Period,
  UserSettings,
  CgmReading,
  DayNote,
} from "../types";
import { DEFAULT_PERIODS, DEFAULT_SETTINGS } from "../types";

// Sync status for local changes
export type SyncStatus = "synced" | "pending" | "conflict";

// Base interface for syncable entities
export interface SyncableEntity {
  _syncStatus?: SyncStatus;
  _localVersion?: number; // Local change counter (timestamp)
  _serverVersion?: number; // Last known server version
  _deletedAt?: string; // Soft delete timestamp (ISO)
  _conflictData?: unknown; // Server data if conflict
  _conflictAt?: string; // When conflict was detected
}

// Extended types with sync fields
export type SyncableLogEntry = LogEntry & SyncableEntity;
export type SyncablePeriod = Period & SyncableEntity;
export type SyncableUserSettings = UserSettings & SyncableEntity;
export type SyncableCgmReading = CgmReading & SyncableEntity;
export type SyncableDayNote = DayNote & SyncableEntity;

// Sync metadata record
export interface SyncMetadata {
  key: "state";
  lastSyncTimestamp?: string; // ISO date of last successful sync
  localVersion: string; // ETag/version from server manifest
  serverChecksum?: string; // Manifest checksum
  tableVersions?: Record<string, number>; // Per-table version tracking
}

/**
 * User-scoped Database class
 * Can represent either own data or monitored user's data
 */
export class DafneUserDB extends Dexie {
  entries!: EntityTable<SyncableLogEntry, "id">;
  periods!: EntityTable<SyncablePeriod, "id">;
  settings!: EntityTable<SyncableUserSettings, "id">;
  cgmReadings!: EntityTable<SyncableCgmReading, "id">;
  dayNotes!: EntityTable<SyncableDayNote, "id">;
  syncMetadata!: EntityTable<SyncMetadata, "key">;

  constructor(dbName: string) {
    // Database name passed directly (can be own or monitored)
    super(dbName);

    // Version 1: Base schema with sync fields
    this.version(1).stores({
      entries:
        "id, timestamp, periodTag, [periodTag+timestamp], _syncStatus, _serverVersion",
      periods: "id, sortOrder, _syncStatus, _serverVersion",
      settings: "id, _syncStatus, _serverVersion",
      cgmReadings:
        "id, timestamp, source, deviceSerial, _syncStatus, _serverVersion",
      syncMetadata: "key",
    });

    // Version 2: Add dayNotes table for personal and clinic notes
    this.version(2).stores({
      entries:
        "id, timestamp, periodTag, [periodTag+timestamp], _syncStatus, _serverVersion",
      periods: "id, sortOrder, _syncStatus, _serverVersion",
      settings: "id, _syncStatus, _serverVersion",
      cgmReadings:
        "id, timestamp, source, deviceSerial, _syncStatus, _serverVersion",
      dayNotes: "id, date, type, authorId, _syncStatus, _serverVersion",
      syncMetadata: "key",
    });
  }
}

/**
 * Database Manager - handles user-specific database instances
 *
 * Supports:
 * - Own database: dafne_{userId}
 * - Monitored databases: dafne_{userId}_monitored_{diabeticUserId}
 */
class DatabaseManager {
  private _currentDb: DafneUserDB | null = null;
  private _currentUserId: string | null = null;
  private _monitoredDbs: Map<string, DafneUserDB> = new Map();

  /**
   * Get database name for user's own data
   */
  private getOwnDbName(userId: string): string {
    return `dafne_${userId}`;
  }

  /**
   * Get database name for monitored user's data
   */
  private getMonitoredDbName(userId: string, diabeticUserId: string): string {
    return `dafne_${userId}_monitored_${diabeticUserId}`;
  }

  /**
   * Get the current database instance
   * @throws Error if not authenticated/initialized
   */
  get db(): DafneUserDB {
    if (!this._currentDb) {
      throw new Error("Database not initialized. Please login first.");
    }
    return this._currentDb;
  }

  /**
   * Get current user ID
   */
  get userId(): string | null {
    return this._currentUserId;
  }

  /**
   * Check if database is initialized
   */
  get isInitialized(): boolean {
    return this._currentDb !== null;
  }

  /**
   * Switch to a user's database
   * Creates database if it doesn't exist
   */
  async switchToUserDatabase(userId: string): Promise<DafneUserDB> {
    // If already using this user's database, just return it
    if (this._currentDb && this._currentUserId === userId) {
      return this._currentDb;
    }

    // Close existing database if different user
    if (this._currentDb && this._currentUserId !== userId) {
      await this.close();
    }

    // Create new database instance using own db name
    const dbName = this.getOwnDbName(userId);
    this._currentDb = new DafneUserDB(dbName);
    this._currentUserId = userId;

    // Open and initialize
    await this._currentDb.open();
    await this.initializeDefaults();
    await this.migrateUnsyncedData();

    return this._currentDb;
  }

  /**
   * Initialize default data for new databases
   */
  private async initializeDefaults(): Promise<void> {
    if (!this._currentDb) return;

    // Check if periods exist
    const periodCount = await this._currentDb.periods.count();
    if (periodCount === 0) {
      const periodsWithSync = DEFAULT_PERIODS.map((p) => ({
        ...p,
        _syncStatus: "pending" as SyncStatus,
        _localVersion: Date.now(),
      }));
      await this._currentDb.periods.bulkAdd(periodsWithSync);
    }

    // Check if settings exist
    const settings = await this._currentDb.settings.get("settings");
    if (!settings) {
      const settingsWithSync: SyncableUserSettings = {
        ...DEFAULT_SETTINGS,
        _syncStatus: "pending",
        _localVersion: Date.now(),
      };
      await this._currentDb.settings.add(settingsWithSync);
    }
  }

  /**
   * Migration: Mark any data without _syncStatus as "pending" so it syncs to server.
   * This handles data created before sync was implemented.
   */
  private async migrateUnsyncedData(): Promise<void> {
    if (!this._currentDb) return;

    const now = Date.now();
    let migratedCount = 0;

    // Migrate entries without _syncStatus
    const unsyncedEntries = await this._currentDb.entries
      .filter((e) => !e._syncStatus)
      .toArray();
    for (const entry of unsyncedEntries) {
      await this._currentDb.entries.update(entry.id, {
        _syncStatus: "pending" as SyncStatus,
        _localVersion: now,
      });
      migratedCount++;
    }

    // Migrate periods without _syncStatus
    const unsyncedPeriods = await this._currentDb.periods
      .filter((p) => !p._syncStatus)
      .toArray();
    for (const period of unsyncedPeriods) {
      await this._currentDb.periods.update(period.id, {
        _syncStatus: "pending" as SyncStatus,
        _localVersion: now,
      });
      migratedCount++;
    }

    // Migrate settings without _syncStatus
    const settings = await this._currentDb.settings.get("settings");
    if (settings && !settings._syncStatus) {
      await this._currentDb.settings.update("settings", {
        _syncStatus: "pending" as SyncStatus,
        _localVersion: now,
      });
      migratedCount++;
    }

    // Migrate CGM readings without _syncStatus
    const unsyncedCgm = await this._currentDb.cgmReadings
      .filter((c) => !c._syncStatus)
      .toArray();
    for (const cgm of unsyncedCgm) {
      await this._currentDb.cgmReadings.update(cgm.id, {
        _syncStatus: "pending" as SyncStatus,
        _localVersion: now,
      });
      migratedCount++;
    }

    if (migratedCount > 0) {
      console.log(`Migrated ${migratedCount} items to pending sync status`);
    }
  }

  /**
   * Close current database connection
   */
  async close(): Promise<void> {
    // Close all monitored databases
    for (const [, monitoredDb] of this._monitoredDbs) {
      monitoredDb.close();
    }
    this._monitoredDbs.clear();

    // Close own database
    if (this._currentDb) {
      this._currentDb.close();
      this._currentDb = null;
      this._currentUserId = null;
    }
  }

  /**
   * Get or create a database for a monitored user
   * Used by monitors/clinicians to view diabetic's data
   */
  async getMonitoredDatabase(diabeticUserId: string): Promise<DafneUserDB> {
    if (!this._currentUserId) {
      throw new Error("Must be logged in to access monitored databases");
    }

    // Check if already open
    let monitoredDb = this._monitoredDbs.get(diabeticUserId);
    if (monitoredDb) {
      return monitoredDb;
    }

    // Create new database instance for monitored user
    const dbName = this.getMonitoredDbName(this._currentUserId, diabeticUserId);
    monitoredDb = new DafneUserDB(dbName);
    await monitoredDb.open();

    this._monitoredDbs.set(diabeticUserId, monitoredDb);
    return monitoredDb;
  }

  /**
   * Close a specific monitored database
   */
  closeMonitoredDatabase(diabeticUserId: string): void {
    const monitoredDb = this._monitoredDbs.get(diabeticUserId);
    if (monitoredDb) {
      monitoredDb.close();
      this._monitoredDbs.delete(diabeticUserId);
    }
  }

  /**
   * Get list of open monitored database IDs
   */
  getOpenMonitoredDatabaseIds(): string[] {
    return Array.from(this._monitoredDbs.keys());
  }

  /**
   * Check if a monitored database is open
   */
  isMonitoredDatabaseOpen(diabeticUserId: string): boolean {
    return this._monitoredDbs.has(diabeticUserId);
  }

  /**
   * Delete a monitored user's database
   */
  async deleteMonitoredDatabase(diabeticUserId: string): Promise<void> {
    if (!this._currentUserId) return;

    // Close if open
    this.closeMonitoredDatabase(diabeticUserId);

    // Delete the database
    const dbName = this.getMonitoredDbName(this._currentUserId, diabeticUserId);
    await Dexie.delete(dbName);
  }

  /**
   * Delete a user's database entirely
   * Used for logout cleanup or data reset
   */
  async deleteUserDatabase(userId: string): Promise<void> {
    const dbName = this.getOwnDbName(userId);

    // Close if it's the current database
    if (this._currentUserId === userId) {
      await this.close();
    }

    // Delete the database
    await Dexie.delete(dbName);
  }

  /**
   * List all Dafne databases (for admin/debug)
   */
  async listAllDatabases(): Promise<string[]> {
    const allDbs = await Dexie.getDatabaseNames();
    return allDbs.filter((name) => name.startsWith("dafne_"));
  }
}

// Singleton instance
export const dbManager = new DatabaseManager();

// Helper to get current database (throws if not initialized)
export function getDb(): DafneUserDB {
  return dbManager.db;
}

// Helper to generate UUID
export function generateId(): string {
  return crypto.randomUUID();
}
